/**
 * ReservoirAerationSpecPage.tsx — Reservoir Bottom-Mounted Diffused Aeration Design
 *
 * Full engineering design tool for submerged fine-bubble aeration systems in
 * reservoirs, lakes, lagoons and constructed wetlands. Covers:
 *   · Diffuser physics — bubble formation (Fritz correlation), rise velocity,
 *     pressure expansion from bed to surface, induced bed shear
 *   · Oxygen transfer — SOTE/m by diffuser type, SOTR, field-corrected AOTR
 *   · Air supply loop — proportional flow, pipe diameter (Darcy-Weisbach),
 *     ring-main vs radial header, standard HDPE DN selection
 *   · Blower design — adiabatic compression power, pressure budget
 *   · Oxygen mass balance — sediment O₂ demand vs AOTR
 *
 * Reference data from aeration.docx (Barcombe Reservoir / South East Water case),
 * Clift, Grace & Weber (1978), ASCE 2-06 (fine-bubble SOTE guidance),
 * WEF MOP 8 (oxygen transfer), Seol et al. (2007) plume model.
 */

import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Wind, Waves, Info, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, ArrowLeft, Save, RotateCcw,
  Gauge, Thermometer, Activity, Layers, Zap, Settings,
  Circle, Ruler, Database, BarChart3, FlaskConical,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';

// ─── Physics Constants (reference values at 20 °C) ───────────────────────────

const G            = 9.81;        // m/s²
const P_ATM        = 101_325;     // Pa
const RHO_AIR_STD  = 1.293;      // kg/m³ at STP (0 °C, 101.325 kPa)
const O2_MASS_FRAC = 0.2314;     // mass fraction O₂ in air
const GAMMA_AIR    = 1.4;        // ratio of specific heats for air
const DO_SAT_20    = 9.09;       // mg/L at 20 °C, 101.325 kPa
// Reference values at 20 °C (for correction factor denominators)
const RHO_W_20  = 998.2;        // kg/m³
const MU_W_20   = 1.002e-3;     // Pa·s
const NU_W_20   = MU_W_20 / RHO_W_20; // m²/s kinematic viscosity at 20 °C

// Standard HDPE pipe OD sizes (DN mm) for air mains
const HDPE_DN = [25, 32, 40, 50, 63, 75, 90, 110, 125, 160, 200, 250, 315, 400];

// ─── Temperature-Dependent Water Properties ───────────────────────────────────
//
// All three properties change significantly with seasonal temperature shifts:
//   Surface tension σ affects bubble formation (Fritz correlation: d_b ∝ σ^1/3)
//   Viscosity μ affects rise velocity (v_b ∝ 1/μ^0.7 in intermediate regime)
//   Density ρ affects hydrostatic pressure and Fritz correlation

/**
 * Water density (kg/m³) vs temperature — Kell (1975) simplified polynomial.
 * Valid 0–40 °C, accuracy ±0.003 kg/m³.
 * ρ_max ≈ 999.97 kg/m³ at 4 °C; ρ = 998.2 kg/m³ at 20 °C.
 */
function waterDensity(tempC: number): number {
  const T = tempC;
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T*T
       + 1.001685e-4*T*T*T - 1.120083e-6*T*T*T*T;
}

/**
 * Dynamic viscosity (Pa·s) vs temperature — Andrade/Vogel equation.
 * μ(T) = 2.414×10⁻⁵ × 10^(247.8 / (T + 133.15)), T in °C.
 * Accuracy ±1% from 0–40 °C.
 * μ at 4 °C ≈ 1.544×10⁻³; at 20 °C ≈ 1.002×10⁻³; at 25 °C ≈ 0.891×10⁻³ Pa·s.
 */
function waterViscosity(tempC: number): number {
  return 2.414e-5 * Math.pow(10, 247.8 / (tempC + 133.15));
}

/**
 * Water surface tension (N/m) vs temperature — linear Vargaftik approximation.
 * σ(T) = 0.07564 − 1.376×10⁻⁴ × T, T in °C.
 * σ at 4 °C ≈ 0.0751; at 20 °C ≈ 0.0729; at 25 °C ≈ 0.0722 N/m.
 * Directly drives bubble diameter via Fritz correlation (d_b ∝ σ^(1/3)).
 */
function waterSurfaceTension(tempC: number): number {
  return Math.max(0.04, 0.07564 - 1.376e-4 * tempC);
}

/** Kinematic viscosity ν = μ/ρ (m²/s) at temperature T. */
function waterKinematicViscosity(tempC: number): number {
  return waterViscosity(tempC) / waterDensity(tempC);
}

// ─── Physics Functions ────────────────────────────────────────────────────────

/** DO saturation (mg/L) at temperature T (°C) — Benson & Krause */
function doSat(tempC: number): number {
  return 468 / (31.6 + tempC);
}

/**
 * Bubble diameter at orifice (mm) — Fritz (1935) + dynamic correction.
 * ALL fluid properties are evaluated at the actual water temperature.
 *
 * Fritz static:  d_b = [6·σ(T)·d_pore / (ρ_w(T)·g)]^(1/3)
 * Warmer water → lower σ → smaller bubbles (dominant effect)
 * Warmer water → lower ρ → slightly larger bubbles (minor)
 * Net: σ dominates; winter bubbles are ~2–4% larger than summer.
 *
 * Dynamic correction for per-pore airflow:
 * Higher flow → higher inertia at orifice → larger departure diameter.
 */
function bubbleDiamMm(
  poreSizeUm: number,
  airflowPerDiffuserNm3h: number,
  activePores: number,
  tempC: number,
): number {
  const d_pore = poreSizeUm / 1e6;
  const sigma  = waterSurfaceTension(tempC);           // temperature-dependent
  const rho_w  = waterDensity(tempC);                  // temperature-dependent
  const d_static = Math.pow((6 * sigma * d_pore) / (rho_w * G), 1 / 3); // m
  const q_pore   = (airflowPerDiffuserNm3h / 3600) / Math.max(1, activePores);
  const q_uLs    = q_pore * 1e9;                       // µL/s per pore
  const corr     = 1 + 0.9 * Math.pow(Math.max(q_uLs, 0.001), 0.25);
  return d_static * corr * 1000;                        // mm
}

/**
 * Terminal bubble rise velocity (m/s) in contaminated reservoir water,
 * corrected for water temperature via kinematic viscosity ratio.
 *
 * Base empirical fit at 20 °C from Clift, Grace & Weber (1978):
 *   fine bubble 1–3 mm → 0.20–0.35 m/s at 20 °C per aeration.docx.
 *
 * Temperature correction (intermediate drag regime, exponent n = 0.7):
 *   v(T) = v_base(20°C) × (ν_20 / ν_T)^0.7
 *
 * Effect: cold water (4 °C, ν = 1.56×10⁻⁶) is ~56% more viscous than at
 * 20 °C (ν = 1.00×10⁻⁶), so rise velocity at 4 °C is ~73% of 20 °C value.
 * Warm summer surface water (22 °C) rises ~6% faster than at 20 °C.
 */
function riseVelocityMs(d_b_mm: number, tempC: number = 20): number {
  // Base fit at 20 °C
  let v_base: number;
  if (d_b_mm <= 0.5)      v_base = 0.13 + 0.16 * d_b_mm;
  else if (d_b_mm <= 3.0) v_base = 0.14 + 0.065 * d_b_mm;
  else if (d_b_mm <= 6.0) v_base = 0.335 + 0.02 * (d_b_mm - 3);
  else                     v_base = 0.395;
  // Kinematic viscosity correction (intermediate regime n ≈ 0.7)
  const nu_T = waterKinematicViscosity(tempC);
  return v_base * Math.pow(NU_W_20 / nu_T, 0.7);
}

/**
 * Bubble diameter at water surface vs at bed — ideal gas pressure expansion.
 * d_surface = d_bed × (P_bed / P_atm)^(1/3)
 * Uses temperature-dependent water density for hydrostatic pressure.
 * A 1 mm bubble at 5 m depth expands to ~1.14 mm at the surface.
 */
function bubbleDiamAtSurface(d_bottom_mm: number, depthM: number, tempC: number): number {
  const rho_w = waterDensity(tempC);
  const P_bed = P_ATM + rho_w * G * depthM;
  return d_bottom_mm * Math.pow(P_bed / P_ATM, 1 / 3);
}

/**
 * Bubble Reynolds number — uses temperature-dependent density and viscosity.
 * Re = ρ_w(T) × v_rise(T) × d_b / μ_w(T)
 */
function bubbleRe(d_b_mm: number, tempC: number): number {
  const d    = d_b_mm / 1000;
  const rho  = waterDensity(tempC);
  const mu   = waterViscosity(tempC);
  return (rho * riseVelocityMs(d_b_mm, tempC) * d) / mu;
}

/**
 * SOTE per metre submergence (%/m) by diffuser type.
 * From ASCE 2-06 and manufacturer data for clean water at 20 °C.
 */
const SOTE_PER_M: Record<string, number> = {
  membrane_disc_epdm: 6.5,
  membrane_disc_ptfe: 7.0,
  membrane_tube_epdm: 6.0,
  ceramic_dome:       5.5,
  hdpe_porous_tube:   5.0,
};

/** Estimated active pore count by diffuser type + size. */
const PORES_BY_TYPE: Record<string, (diamMm: number) => number> = {
  membrane_disc_epdm:  (d) => Math.round(0.35 * Math.PI * (d / 2) ** 2),  // ~350 pores/cm²
  membrane_disc_ptfe:  (d) => Math.round(0.40 * Math.PI * (d / 2) ** 2),
  membrane_tube_epdm:  (d) => Math.round(0.30 * d * 500),                  // d=length mm, 500mm std tube
  ceramic_dome:        (d) => Math.round(0.10 * Math.PI * (d / 2) ** 2),
  hdpe_porous_tube:    (d) => Math.round(0.08 * d * 500),
};

/** SOTR (kg O₂/h) from total airflow and SOTE. */
function calcSOTR(totalNm3h: number, soteDecimal: number): number {
  return totalNm3h * RHO_AIR_STD * O2_MASS_FRAC * soteDecimal;
}

/** AOTR (kg O₂/h) with process corrections. */
function calcAOTR(
  sotr: number, alpha: number, beta: number,
  tempC: number, currentDoMgL: number,
): number {
  const cs_T  = doSat(tempC);
  const theta = Math.pow(1.024, tempC - 20);
  return sotr * alpha * beta * theta * Math.max(0, cs_T - currentDoMgL) / DO_SAT_20;
}

/**
 * Pipe inner diameter (m) needed for target air velocity.
 * ringMain=true → each half of ring carries Q/2.
 * Uses actual volumetric flow at mean pipe pressure.
 */
function pipeInnerDiam(
  totalNm3h: number,
  submDepthM: number,
  velocityMs: number,
  ringMain: boolean,
): number {
  const P_mid = P_ATM + waterDensity(20) * G * submDepthM * 0.5; // mid-point (20°C reference)
  const Q_std = totalNm3h / 3600;                       // Nm³/s
  const Q_act = Q_std * (P_ATM / P_mid);               // m³/s at mid-pipe pressure
  const Q_half = ringMain ? Q_act / 2 : Q_act;
  return Math.sqrt((4 * Q_half) / (Math.PI * velocityMs));
}

/**
 * Select next standard HDPE DN ≥ required ID.
 * HDPE SDR11: ID ≈ OD × 0.833; so OD_needed = ID / 0.833
 */
function selectDN(innerDiamM: number): number {
  const id_mm = innerDiamM * 1000;
  const od_needed = id_mm / 0.833;
  return HDPE_DN.find((dn) => dn >= od_needed) ?? 400;
}

/** Actual ID of selected DN pipe (SDR11 HDPE, mm). */
function actualID(dn: number): number {
  return dn * 0.833;
}

/** Darcy-Weisbach friction loss (kPa) in air main. */
function pipeFrictionKpa(
  Q_act_m3s: number,
  lengthM: number,
  id_m: number,
  f: number,
  rho_air: number,
): number {
  if (id_m <= 0 || Q_act_m3s <= 0) return 0;
  const A = Math.PI * id_m ** 2 / 4;
  const v = Q_act_m3s / A;
  return (f * (lengthM / id_m) * rho_air * v * v / 2) / 1000;
}

/** Air density at operating pressure (mid-pipe, kg/m³). */
function rhoAirAtPressure(submDepthM: number): number {
  const P_mid = P_ATM + waterDensity(20) * G * submDepthM * 0.5;
  return RHO_AIR_STD * (P_mid / P_ATM) * (273.15 / 293.15); // correct to 20 °C
}

/**
 * Blower shaft power (kW) — adiabatic compression.
 * More accurate than Q×ΔP for low-pressure positive-displacement blowers.
 */
function blowerPowerKw(
  totalNm3h: number,
  submDepthM: number,
  diffuserDropKpa: number,
  safetyPct: number,
  efficiency: number,
): number {
  const Q_std  = totalNm3h / 3600;   // Nm³/s
  const P_in   = P_ATM;
  const ΔP_static = RHO_W_20 * G * submDepthM; // Pa
  const P_out  = P_in + (ΔP_static + diffuserDropKpa * 1000) * (1 + safetyPct / 100);
  const exp    = (GAMMA_AIR - 1) / GAMMA_AIR;
  const W      = (GAMMA_AIR / (GAMMA_AIR - 1)) *
                 P_in * Q_std *
                 (Math.pow(P_out / P_in, exp) - 1) / efficiency;
  return W / 1000;
}

/**
 * Induced horizontal water velocity at bed (m/s) from bubble plume.
 * Simplified from Seol et al. (2007) integral plume model.
 * v_horizontal ≈ 0.15 × U_plume / √H
 * U_plume = 1.46 × (B × H)^(1/3), B = g × q_actual
 */
function inducedBedVelocity(
  airflowPerDiffuserNm3h: number,
  depthM: number,
  tempC: number,
): number {
  const rho_w = waterDensity(tempC);
  const P_bed = P_ATM + rho_w * G * depthM;
  const q_act = (airflowPerDiffuserNm3h / 3600) * (P_ATM / P_bed); // m³/s per diffuser at depth
  const B     = G * q_act;                                          // buoyancy flux m⁴/s³
  const U_p   = 1.46 * Math.pow(B * depthM, 1 / 3);
  return 0.15 * U_p / Math.sqrt(Math.max(depthM, 1));
}

// ─── Design State ─────────────────────────────────────────────────────────────

interface AerationDesign {
  // Zone
  zoneName:       string;
  zoneAreaHa:     number;
  waterDepthM:    number;

  // Diffuser
  diffuserType:   string;
  diffuserDiamMm: number;   // disc diameter (mm)
  poreSizeUm:     number;   // µm
  airflowPerDiffNm3h: number;
  diffuserDropKpa:    number;
  mountingHeightM:    number;

  // Loop layout
  diffusersPerLoop: number;
  numberOfLoops:    number;
  spacingM:         number;
  ringMain:         boolean;

  // Air main
  pipeVelocityMs:    number;
  pipeRunLengthM:    number;
  pipeFrictionFactor:number;

  // Blower
  blowerType:      string;
  blowerEfficiency:number;
  safetyMarginPct: number;

  // Process conditions
  waterTempC:     number;
  currentDoMgL:   number;
  alphaFactor:    number;
  betaFactor:     number;

  // Oxygen demand
  sodGO2m2d:      number;   // sediment O₂ demand g/m²/day
  waterBodMgLd:   number;   // water column BOD demand mg/L/day

  // Sediment characterisation (for Shields disturbance factor)
  sedimentD50Um:  number;   // median particle size µm
  sedimentRho:    number;   // particle density kg/m³
}

const DEFAULTS: AerationDesign = {
  zoneName:       'Zone A',
  zoneAreaHa:     1.5,
  waterDepthM:    5.0,
  diffuserType:   'membrane_disc_epdm',
  diffuserDiamMm: 300,
  poreSizeUm:     80,
  airflowPerDiffNm3h: 1.2,
  diffuserDropKpa:    1.5,
  mountingHeightM:    0.4,
  diffusersPerLoop:   10,
  numberOfLoops:      3,
  spacingM:           15,
  ringMain:           true,
  pipeVelocityMs:     10,
  pipeRunLengthM:     80,
  pipeFrictionFactor: 0.022,
  blowerType:        'roots',
  blowerEfficiency:  0.70,
  safetyMarginPct:   10,
  waterTempC:        15,
  currentDoMgL:      2.0,
  alphaFactor:       0.70,
  betaFactor:        0.98,
  sodGO2m2d:         0.5,
  waterBodMgLd:      0.5,
  sedimentD50Um:     100,   // fine silt — typical UK lowland reservoir
  sedimentRho:       2650,  // silica/clay particle density kg/m³
};

const DIFFUSER_TYPES = [
  { value: 'membrane_disc_epdm', label: 'EPDM Membrane Disc',        sote: 6.5, typicalPoreUm:  80, typicalDiamMm: 250, notes: 'Most common in reservoirs. Fine pores (50–250 µm), SAE 5–7 kgO₂/kWh. Handles variable flow. Low fouling.' },
  { value: 'membrane_disc_ptfe', label: 'PTFE Membrane Disc',        sote: 7.0, typicalPoreUm:  60, typicalDiamMm: 230, notes: 'High chemical resistance. Very fine pores (40–150 µm). Best OTE, higher cost. Suited to aggressive water.' },
  { value: 'membrane_tube_epdm', label: 'EPDM Membrane Tube',        sote: 6.0, typicalPoreUm: 100, typicalDiamMm: 1500, notes: 'Tubular form, easy to mount on frame at bed level. Good for long narrow zones.' },
  { value: 'ceramic_dome',       label: 'Ceramic Dome Diffuser',     sote: 5.5, typicalPoreUm: 150, typicalDiamMm: 210, notes: 'Very robust. Pore size 100–400 µm. Lower OTE than membrane. Good for high-temperature water.' },
  { value: 'hdpe_porous_tube',   label: 'HDPE Porous Tube',         sote: 5.0, typicalPoreUm: 200, typicalDiamMm: 1800, notes: 'Low cost, durable. Coarser pores → larger bubbles. Suited to mixing-priority applications.' },
];

const BLOWER_TYPES = [
  { value: 'roots',       label: 'Roots / Rotary Lobe', efficiency: 0.65, notes: 'Most common for <0.5 bar. Robust, low-maintenance. Suitable for 1–200 kW.' },
  { value: 'screw',       label: 'Screw Blower',         efficiency: 0.78, notes: 'Higher efficiency than Roots. Good for continuous duty, 5–500 kW.' },
  { value: 'centrifugal', label: 'Turbo / Centrifugal',  efficiency: 0.82, notes: 'Best efficiency at scale. Requires surge protection. Preferred >50 kW.' },
];

// ─── Seasonal UK Reservoir Raw Water Quality Profiles ────────────────────────
//
// Based on UK Environment Agency monitoring, UKCEH lake datasets, DWI data,
// and peer-reviewed literature for lowland eutrophic reservoirs in England
// (references: Esthwaite Water 1945–2013; Grafham Water EA classification;
// Woolway et al. 2021 Nature Comms; Müller et al. 2012 ACS ES&T; ASCE 2-06).
// Values represent typical/median for eutrophic lowland UK reservoir (SE England).

export interface SeasonalProfile {
  id: string;
  label: string;
  months: string;
  icon: string;
  accent: string;        // tailwind color class
  accentHex: string;
  description: string;
  stratification: string;

  // Parameters that feed directly into aeration calculations
  waterTempC:      number;   // epilimnion / design layer temperature
  currentDoMgL:    number;   // design layer DO — hypolimnion in summer (worst case for aeration sizing)
  alphaFactor:     number;   // process water O₂ transfer correction
  betaFactor:      number;
  sodGO2m2d:       number;   // sediment oxygen demand g O₂/m²/day
  waterBodMgLd:    number;   // water column BOD demand mg/L/day (converted to kg/h internally)

  // Informational raw water quality parameters
  tempRangeC:      [number, number];
  doSurface:       [number, number];   // surface / epilimnion
  doHypo:          [number, number];   // hypolimnion
  doSatSurface:    [number, number];   // %
  bod5:            [number, number];
  cod:             [number, number];
  tpUgL:           [number, number];
  tnMgL:           [number, number];
  nh4Surface:      [number, number];   // mg/L, surface
  nh4Hypo:         [number, number];   // mg/L, hypolimnion
  turbidity:       [number, number];
  pH:              [number, number];
  chlaUgL:         [number, number];
  sodRange:        [number, number];   // g O₂/m²/day
  alphaRange:      [number, number];

  // Engineering notes
  designNote: string;
}

export const SEASONAL_PROFILES: SeasonalProfile[] = [
  {
    id: 'winter',
    label: 'Winter',
    months: 'December – February',
    icon: '❄️',
    accent: 'text-sky-400',
    accentHex: '#38bdf8',
    description: 'Cold, well-mixed water. High DO from reduced biology. Low SOD. Minimal algae.',
    stratification: 'None — fully mixed to bed',
    // Calculation inputs
    waterTempC:   4,
    currentDoMgL: 11.0,   // surface/whole column (mixed)
    alphaFactor:  0.90,
    betaFactor:   0.98,
    sodGO2m2d:    0.45,
    waterBodMgLd: 0.20,
    // Informational ranges
    tempRangeC:   [2, 6],
    doSurface:    [10.0, 12.0],
    doHypo:       [9.0, 11.0],
    doSatSurface: [90, 105],
    bod5:         [1.5, 3.5],
    cod:          [10, 25],
    tpUgL:        [50, 150],
    tnMgL:        [3.0, 8.0],
    nh4Surface:   [0.05, 0.30],
    nh4Hypo:      [0.10, 0.50],
    turbidity:    [2, 50],
    pH:           [7.2, 7.8],
    chlaUgL:      [1, 5],
    sodRange:     [0.30, 0.80],
    alphaRange:   [0.85, 0.95],
    designNote: 'Cold water holds DO well — lower AOTR driving force. SOD is low (biological activity suppressed at <6 °C). Aeration may still be needed to prevent stagnation in pockets. Blower pressure slightly higher due to denser cold air.',
  },
  {
    id: 'spring',
    label: 'Spring',
    months: 'March – May',
    icon: '🌱',
    accent: 'text-emerald-400',
    accentHex: '#34d399',
    description: 'Warming water, diatom bloom onset. DO elevated by photosynthesis. SOD rising.',
    stratification: 'Onset late April–May — design for full depth until stable',
    waterTempC:   10,
    currentDoMgL: 10.5,
    alphaFactor:  0.85,
    betaFactor:   0.98,
    sodGO2m2d:    0.80,
    waterBodMgLd: 0.30,
    tempRangeC:   [6, 14],
    doSurface:    [9.0, 12.0],
    doHypo:       [7.0, 10.0],
    doSatSurface: [95, 120],
    bod5:         [1.5, 3.0],
    cod:          [10, 20],
    tpUgL:        [40, 100],
    tnMgL:        [2.0, 6.0],
    nh4Surface:   [0.02, 0.15],
    nh4Hypo:      [0.10, 0.40],
    turbidity:    [1, 20],
    pH:           [7.5, 8.5],
    chlaUgL:      [5, 30],
    sodRange:     [0.50, 1.20],
    alphaRange:   [0.80, 0.90],
    designNote: 'DO may be above saturation at surface from algal photosynthesis — AOTR driving force reduced. Design diffuser system for the developing thermocline depth (late spring ≈ 4–6 m). SOD increasing as temperatures rise.',
  },
  {
    id: 'summer',
    label: 'Summer',
    months: 'June – August',
    icon: '☀️',
    accent: 'text-amber-400',
    accentHex: '#fbbf24',
    description: 'Critical design season. Hypolimnion anoxic. Peak SOD. Algal bloom. Design for hypolimnetic conditions.',
    stratification: 'Fully stratified. Epilimnion 3–8 m. Thermocline 4–10 m. Hypolimnion anoxic from July.',
    waterTempC:   8,    // hypolimnion temperature (design layer for anoxia prevention)
    currentDoMgL: 1.5,  // hypolimnion — near-anoxic (worst case driving force for aeration)
    alphaFactor:  0.75,
    betaFactor:   0.98,
    sodGO2m2d:    1.80,
    waterBodMgLd: 0.60,
    tempRangeC:   [14, 22],   // surface range; hypo stays 5–10 °C
    doSurface:    [7.0, 10.0],
    doHypo:       [0.0, 3.0],
    doSatSurface: [90, 130],
    bod5:         [2.0, 5.0],
    cod:          [15, 30],
    tpUgL:        [20, 80],
    tnMgL:        [0.5, 3.0],
    nh4Surface:   [0.01, 0.10],
    nh4Hypo:      [0.50, 5.00],
    turbidity:    [1, 30],
    pH:           [8.0, 9.5],
    chlaUgL:      [10, 150],
    sodRange:     [1.00, 2.50],
    alphaRange:   [0.70, 0.85],
    designNote: '⚠ CRITICAL DESIGN SEASON. Design to prevent hypolimnetic anoxia — use hypolimnion temperature (5–10 °C) and near-zero DO as design conditions, not surface values. SOD at peak. Alpha depressed by algal surfactants (EPS). NH₄ release from anoxic sediment can exceed 2 mg/L in hypolimnion.',
  },
  {
    id: 'autumn',
    label: 'Autumn',
    months: 'September – November',
    icon: '🍂',
    accent: 'text-orange-400',
    accentHex: '#fb923c',
    description: 'Autumn overturn mixes hypolimnetic water. DO partially recovers. SOD decreasing with temperature.',
    stratification: 'Breaking down Oct–Nov. Overturn mixes NH₄ and depleted DO to full depth.',
    waterTempC:   12,
    currentDoMgL: 7.0,   // post-overturn mixed column
    alphaFactor:  0.80,
    betaFactor:   0.98,
    sodGO2m2d:    1.00,
    waterBodMgLd: 0.40,
    tempRangeC:   [8, 15],
    doSurface:    [9.0, 11.0],
    doHypo:       [7.0, 10.0],
    doSatSurface: [90, 110],
    bod5:         [2.0, 4.0],
    cod:          [15, 25],
    tpUgL:        [60, 150],
    tnMgL:        [1.5, 5.0],
    nh4Surface:   [0.05, 0.50],
    nh4Hypo:      [0.10, 1.00],
    turbidity:    [2, 40],
    pH:           [7.5, 8.5],
    chlaUgL:      [5, 50],
    sodRange:     [0.60, 1.50],
    alphaRange:   [0.75, 0.85],
    designNote: 'Post-overturn NH₄ pulse can reach full water column — aeration helps oxidise ammonium. DO recovering but still deficient relative to saturation (good AOTR driving force). TP elevated after release from formerly anoxic sediment. SOD falling as temperature drops.',
  },
];

// ─── Seasonal Selector Component ─────────────────────────────────────────────

function SeasonCard({ profile, selected, onSelect }:
  { profile: SeasonalProfile; selected: boolean; onSelect: () => void }) {
  return (
    <button onClick={onSelect}
      className={`text-left px-4 py-3 rounded-2xl border transition-all relative ${
        selected
          ? 'bg-rb-bg border-current shadow-md'
          : 'bg-rb-elevated border-slate-700/60 hover:border-slate-500'
      }`}
      style={selected ? { borderColor: profile.accentHex } : {}}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-lg leading-none">{profile.icon}</span>
        <span className={`text-sm font-bold ${selected ? profile.accent : 'text-slate-300'}`}>{profile.label}</span>
        {selected && (
          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: profile.accentHex + '22', color: profile.accentHex }}>ACTIVE</span>
        )}
      </div>
      <div className="text-[10px] text-slate-500 mb-2">{profile.months}</div>
      <div className="text-[10px] text-slate-400 leading-relaxed">{profile.description}</div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
        <div className="text-slate-600">Temp <span style={{ color: profile.accentHex }} className="font-mono font-bold">{profile.waterTempC}°C</span></div>
        <div className="text-slate-600">DO <span style={{ color: profile.accentHex }} className="font-mono font-bold">{profile.currentDoMgL} mg/L</span></div>
        <div className="text-slate-600">α <span style={{ color: profile.accentHex }} className="font-mono font-bold">{profile.alphaFactor}</span></div>
        <div className="text-slate-600">SOD <span style={{ color: profile.accentHex }} className="font-mono font-bold">{profile.sodGO2m2d} g/m²/d</span></div>
      </div>
    </button>
  );
}

function SeasonalDataPanel({ profile }: { profile: SeasonalProfile }) {
  const r = (lo: number, hi: number, unit: string, dp = 1) =>
    `${lo.toFixed(dp)}–${hi.toFixed(dp)} ${unit}`;

  const rows: { label: string; surface: string; hypo: string; note?: string }[] = [
    { label: 'Temperature',       surface: r(...profile.tempRangeC, '°C', 0),     hypo: '5–10 °C (stratified summer)', note: 'Hypo stable in summer; mixes in winter' },
    { label: 'DO (mg/L)',         surface: r(...profile.doSurface, 'mg/L'),        hypo: r(...profile.doHypo, 'mg/L'),   note: 'Design to hypolimnion value in summer' },
    { label: 'DO saturation',     surface: r(...profile.doSatSurface, '%', 0),     hypo: 'Often 0–30%',                  note: 'Algal bloom can cause supersaturation' },
    { label: 'BOD₅',             surface: r(...profile.bod5, 'mg/L'),             hypo: '—',                            note: 'Algal biomass contributes in summer' },
    { label: 'COD',               surface: r(...profile.cod, 'mg/L'),             hypo: '—',                            note: 'Higher in summer / autumn algal decay' },
    { label: 'Total Phosphorus',  surface: r(...profile.tpUgL, 'µg/L', 0),        hypo: 'Higher (anoxic release)',       note: 'Internal loading peak in summer anoxia' },
    { label: 'Total Nitrogen',    surface: r(...profile.tnMgL, 'mg/L'),           hypo: 'Low (denitrification)',         note: 'Nitrate depleted by summer' },
    { label: 'Ammonium NH₄',     surface: r(...profile.nh4Surface, 'mg/L'),      hypo: r(...profile.nh4Hypo, 'mg/L'),   note: 'High hypo NH₄ released under anoxia' },
    { label: 'Turbidity',         surface: r(...profile.turbidity, 'NTU', 0),     hypo: '—',                            note: 'Storm events can spike to 100+ NTU' },
    { label: 'pH',                surface: r(...profile.pH, ''),                  hypo: '6.5–7.2 (CO₂ accumulation)',   note: 'Algal photosynthesis drives surface pH up' },
    { label: 'Chlorophyll-a',     surface: r(...profile.chlaUgL, 'µg/L', 0),     hypo: 'Low',                          note: 'Peak bloom can exceed 100 µg/L' },
    { label: 'SOD',               surface: r(...profile.sodRange, 'g/m²/d'),      hypo: '—',                            note: 'Temperature-dependent Q₁₀ ≈ 2.2' },
    { label: 'Alpha factor (α)',  surface: r(...profile.alphaRange, ''),          hypo: '—',                            note: 'Algal surfactants depress α in summer' },
  ];

  return (
    <div className="mt-4 rounded-xl overflow-hidden border border-slate-800/50">
      <div className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider"
        style={{ backgroundColor: profile.accentHex + '11', borderBottom: `1px solid ${profile.accentHex}30` }}>
        {profile.icon} {profile.label} — Typical UK Lowland Eutrophic Reservoir Raw Water Quality
        <span className="ml-2 font-normal text-slate-600">Source: EA / UKCEH / DWI monitoring data; Müller et al. 2012; Woolway et al. 2021</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[600px]">
          <thead>
            <tr className="text-[10px] text-slate-600 uppercase border-b border-slate-800/50 bg-rb-elevated">
              <th className="text-left px-3 py-1.5 w-40">Parameter</th>
              <th className="text-left px-3 py-1.5">Surface / Epilimnion</th>
              <th className="text-left px-3 py-1.5">Hypolimnion</th>
              <th className="text-left px-3 py-1.5 text-slate-700">Design Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} className={`border-b border-slate-800/30 ${i % 2 === 0 ? 'bg-rb-elevated/30' : ''}`}>
                <td className="px-3 py-1.5 text-slate-400 font-medium">{row.label}</td>
                <td className="px-3 py-1.5 text-slate-300 font-mono text-[11px]">{row.surface}</td>
                <td className="px-3 py-1.5 text-slate-400 font-mono text-[11px]">{row.hypo}</td>
                <td className="px-3 py-1.5 text-slate-600 text-[10px]">{row.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {profile.designNote && (
        <div className="px-3 py-2 text-[11px] border-t border-slate-800/50"
          style={{ backgroundColor: profile.accentHex + '08', color: profile.accentHex + 'cc' }}>
          {profile.designNote}
        </div>
      )}
      <div className="px-3 py-1.5 text-[9px] text-slate-700 border-t border-slate-800/50">
        Stratification: <span className="text-slate-600">{profile.stratification}</span>
      </div>
    </div>
  );
}

// ─── Helper: metric card ──────────────────────────────────────────────────────

function KpiCard({ label, value, unit, color = '#06b6d4', warn = false, tip }:
  { label: string; value: string; unit: string; color?: string; warn?: boolean; tip?: string }) {
  return (
    <div className={`rb-section px-4 py-3 relative ${warn ? 'border-amber-500/40' : ''}`}>
      {tip && (
        <div className="absolute top-2 right-2 group">
          <Info className="w-3 h-3 text-slate-600 cursor-help" />
          <div className="hidden group-hover:block absolute right-0 top-5 z-20 w-52 p-2 text-[10px] text-slate-300 bg-rb-surface border border-slate-700 rounded-lg shadow-xl">{tip}</div>
        </div>
      )}
      <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-xl font-bold font-mono leading-none" style={{ color }}>{value}</div>
      <div className="text-[10px] text-slate-600 mt-0.5">{unit}</div>
      {warn && <AlertTriangle className="absolute bottom-2 right-2 w-3 h-3 text-amber-400" />}
    </div>
  );
}

function NumInput({ label, value, onChange, min, max, step, unit, tip, wide }:
  { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; tip?: string; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
        {label}{unit && <span className="font-normal text-slate-600 ml-1 normal-case">({unit})</span>}
      </label>
      {tip && <p className="text-[9px] text-slate-600 mb-1">{tip}</p>}
      <input
        type="number" value={value} step={step ?? 0.1} min={min} max={max}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500"
      />
    </div>
  );
}

function SectionCard({ title, icon: Icon, iconColor, children }:
  { title: string; icon: React.ElementType; iconColor: string; children: React.ReactNode }) {
  return (
    <div className="rb-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── Check row component ──────────────────────────────────────────────────────

function CheckRow({ label, ok, value, limit, unit }:
  { label: string; ok: boolean; value: string; limit: string; unit: string }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-xl ${ok ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-amber-500/8 border border-amber-500/30'}`}>
      {ok
        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
        : <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />}
      <span className="text-xs text-slate-300 flex-1">{label}</span>
      <span className={`text-xs font-mono font-bold ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>{value} {unit}</span>
      <span className="text-[10px] text-slate-600">{ok ? '≤' : '>'} {limit} {unit}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReservoirAerationSpecPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const siteName  = (location.state as { siteName?: string } | null)?.siteName ?? '';

  const [d, setD]         = useState<AerationDesign>(DEFAULTS);
  const [activeTab, setActiveTab] = useState<'design' | 'airblower' | 'physics'>('design');
  const [expandedInfo, setExpandedInfo] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [showSeasonData, setShowSeasonData] = useState(false);

  // ── Custom diffuser detection (reactive across pages) ────────────────────────
  const [customDiff, setCustomDiff] = useState<{
    label: string; sote: number; poreSize: number; diameterMm: number;
    activePores: number; shape: string; notes: string;
    lengthMm?: number; widthMm?: number;
  } | null>(() => {
    try {
      const raw = localStorage.getItem('rb_custom_diffuser_export_v1')
        ?? sessionStorage.getItem('rb_custom_diffuser_export_v1');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  useEffect(() => {
    const refresh = () => {
      try {
        const raw = localStorage.getItem('rb_custom_diffuser_export_v1')
          ?? sessionStorage.getItem('rb_custom_diffuser_export_v1');
        setCustomDiff(raw ? JSON.parse(raw) : null);
      } catch { /* ignore */ }
    };
    const onChange = (e: Event) => {
      const ce = e as CustomEvent<{ key: string }>;
      if (!ce.detail || ce.detail.key === 'rb_custom_diffuser_export_v1') refresh();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'rb_custom_diffuser_export_v1') refresh();
    };
    window.addEventListener('rb-persist-changed', onChange);
    window.addEventListener('storage', onStorage);
    // Also refresh whenever the page becomes visible (returning from custom designer)
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('rb-persist-changed', onChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Build dynamic types list — adds a "Custom Diffuser" option whenever the
  // CustomDiffuser page has exported a design.
  const ALL_DIFFUSER_TYPES = useMemo(() => {
    const base = [...DIFFUSER_TYPES];
    if (customDiff) {
      base.push({
        value: 'custom',
        label: `★ Custom: ${customDiff.label}`,
        sote: customDiff.sote,
        notes: customDiff.notes,
      });
    }
    return base;
  }, [customDiff]);

  const upd = (partial: Partial<AerationDesign>) => setD(prev => ({ ...prev, ...partial }));

  const applySeason = (profile: SeasonalProfile) => {
    setSelectedSeason(profile.id);
    setShowSeasonData(true);
    upd({
      waterTempC:   profile.waterTempC,
      currentDoMgL: profile.currentDoMgL,
      alphaFactor:  profile.alphaFactor,
      betaFactor:   profile.betaFactor,
      sodGO2m2d:    profile.sodGO2m2d,
      waterBodMgLd: profile.waterBodMgLd,
    });
    toast.success(`${profile.icon} ${profile.label} profile applied — process conditions updated`);
  };

  // ── Derived design variables ────────────────────────────────────────────────

  const results = useMemo(() => {
    const totalDiffusers  = d.diffusersPerLoop * d.numberOfLoops;
    const effSubm         = Math.max(0.5, d.waterDepthM - d.mountingHeightM);
    // Look up in ALL_DIFFUSER_TYPES (includes custom). Fall back to first if missing.
    const diffType        = ALL_DIFFUSER_TYPES.find(t => t.value === d.diffuserType) ?? ALL_DIFFUSER_TYPES[0];

    // Active pores per diffuser - TYPE-AWARE; pick up custom diffuser if present
    let activePores: number;
    let activePoreSize = d.poreSizeUm;
    try {
      const customRaw = localStorage.getItem('rb_custom_diffuser_export_v1')
        ?? sessionStorage.getItem('rb_custom_diffuser_export_v1');
      const custom = customRaw ? JSON.parse(customRaw) : null;
      if (d.diffuserType === 'custom' && custom) {
        activePores   = Math.max(1, custom.activePores ?? 100);
        activePoreSize = custom.poreSize ?? d.poreSizeUm;
      } else if (d.diffuserType === 'hdpe_porous_tube' || d.diffuserType === 'epdm_porous_tube') {
        // Use tube formula: pores per cm of tube length
        activePores = Math.max(1, Math.round(8 * 1.8 * 100)); // 8/cm x 1.8m default
      } else {
        const poresFn = PORES_BY_TYPE[d.diffuserType] ?? ((x: number) => Math.round(0.3 * Math.PI * (x / 2) ** 2));
        activePores = Math.max(1, poresFn(d.diffuserDiamMm));
      }
    } catch {
      const poresFn = PORES_BY_TYPE[d.diffuserType] ?? ((x: number) => Math.round(0.3 * Math.PI * (x / 2) ** 2));
      activePores = Math.max(1, poresFn(d.diffuserDiamMm));
    }

    // Temperature-dependent water properties
    const T          = d.waterTempC;
    const rho_w      = waterDensity(T);
    const mu_w       = waterViscosity(T);
    const sigma_w    = waterSurfaceTension(T);
    const nu_w       = mu_w / rho_w;

    // Bubble physics at orifice (bottom of reservoir) — all temperature-corrected
    const d_b_mm     = bubbleDiamMm(activePoreSize, d.airflowPerDiffNm3h, activePores, T);
    const d_b_surf   = bubbleDiamAtSurface(d_b_mm, effSubm, T);
    const v_rise_b   = riseVelocityMs(d_b_mm, T);     // at bed, cold/warm water
    const v_rise_s   = riseVelocityMs(d_b_surf, T);   // at surface (larger bubble)
    const Re_b       = bubbleRe(d_b_mm, T);
    const riseTimeS  = effSubm / ((v_rise_b + v_rise_s) / 2);
    const P_bed      = P_ATM + rho_w * G * effSubm;

    // Pressure at mid-rise (average) for O₂ concentration correction
    const P_mid      = P_ATM + rho_w * G * effSubm / 2;
    const Cs_mid     = doSat(d.waterTempC) * (P_mid / P_ATM); // pressure-corrected saturation

    // Oxygen transfer
    const sote_pct   = Math.min(99, diffType.sote * effSubm);  // SOTE %
    const totalNm3h  = totalDiffusers * d.airflowPerDiffNm3h;
    const sotr       = calcSOTR(totalNm3h, sote_pct / 100);
    const aotr       = calcAOTR(sotr, d.alphaFactor, d.betaFactor, d.waterTempC, d.currentDoMgL);

    // Oxygen demand
    const zoneAreaM2 = d.zoneAreaHa * 1e4;
    const zoneVolM3  = zoneAreaM2 * d.waterDepthM;
    const sodKgO2h   = (d.sodGO2m2d * zoneAreaM2 / 1000) / 24; // kg/h
    const bodKgO2h   = (d.waterBodMgLd * zoneVolM3 / 1000) / 24;
    const totalDemandKgO2h = sodKgO2h + bodKgO2h;
    const oxygenSurplus  = aotr - totalDemandKgO2h;             // kg/h, positive = ok

    // Coverage
    const coveragePerDiff = Math.PI * (d.spacingM / 2) ** 2;    // m² per diffuser (circle of influence)
    const totalCoverage   = Math.min(zoneAreaM2, totalDiffusers * coveragePerDiff);
    const coveragePct     = (totalCoverage / zoneAreaM2) * 100;

    // Power density
    const blower     = BLOWER_TYPES.find(b => b.value === d.blowerType)!;
    const bEff       = d.blowerEfficiency > 0 ? d.blowerEfficiency : blower.efficiency;
    const ΔP_static  = (rho_w * G * effSubm) / 1000;  // kPa
    const Q_std_m3s  = totalNm3h / 3600;
    const Q_act_m3s  = Q_std_m3s * (P_ATM / P_bed);
    const rho_air    = rhoAirAtPressure(effSubm);

    // Pipe sizing (ring main or single feed)
    const id_needed  = pipeInnerDiam(totalNm3h, effSubm, d.pipeVelocityMs, d.ringMain);
    const dn_sel     = selectDN(id_needed);
    const id_actual  = actualID(dn_sel) / 1000;         // m
    const Q_half_act = d.ringMain ? Q_act_m3s / 2 : Q_act_m3s;
    const v_pipe_actual = Q_half_act / (Math.PI * id_actual ** 2 / 4);
    const frictionLossKpa = pipeFrictionKpa(
      Q_half_act, d.pipeRunLengthM, id_actual, d.pipeFrictionFactor, rho_air
    );

    // Blower pressure budget
    const blowerPressKpa = ΔP_static + frictionLossKpa + d.diffuserDropKpa;
    const blowerPressTotal = blowerPressKpa * (1 + d.safetyMarginPct / 100);
    const kw = blowerPowerKw(totalNm3h, effSubm, d.diffuserDropKpa, d.safetyMarginPct, bEff);
    const sae = aotr > 0 && kw > 0 ? aotr / kw : 0;  // kg O₂/kWh
    const powerDensWm3 = kw > 0 && zoneVolM3 > 0 ? (kw * 1000) / zoneVolM3 : 0;

    // Induced bed velocity (sediment check) — Seol et al. (2007)
    const v_bed = inducedBedVelocity(d.airflowPerDiffNm3h, effSubm, T);

    // ── Bed sedimentation disturbance (Shields criterion) ────────────────────
    const d50_m      = d.sedimentD50Um / 1e6;
    const C_D_bed    = 0.01;                            // bed drag coefficient (rough turbulent)
    const tau_bed    = rho_w * C_D_bed * v_bed ** 2;   // Pa
    const u_star     = Math.sqrt(tau_bed / rho_w);      // shear velocity m/s
    const shields    = tau_bed / ((d.sedimentRho - rho_w) * G * Math.max(d50_m, 1e-8));
    const shields_cr = 0.047;                            // Shields (1936) critical value
    const disturbRatio = shields / shields_cr;
    const resuspRisk = disturbRatio > 1.5 ? 'High' : disturbRatio > 1.0 ? 'Moderate'
                     : disturbRatio > 0.5 ? 'Low' : 'Negligible';
    const resuspColor= disturbRatio > 1.5 ? '#ef4444' : disturbRatio > 1.0 ? '#f59e0b'
                     : disturbRatio > 0.5 ? '#eab308' : '#10b981';

    // Bubble size correction: near-bed turbulence → orifice coalescence (Loubière & Hébrard 2003)
    const turbCorr_b       = Math.min(0.15, 0.08 * Math.min(disturbRatio, 2));
    const d_b_mm_dist      = d_b_mm * (1 + turbCorr_b);
    const d_b_surf_dist    = bubbleDiamAtSurface(d_b_mm_dist, effSubm, T);

    // Enhanced SOD from sediment resuspension
    // +50% per unit disturbRatio above threshold, capped at 3×
    const sodResuspFactor  = disturbRatio > 1 ? Math.min(3.0, 1 + 0.5 * (disturbRatio - 1)) : 1.0;
    const effectiveSOD_gm2 = d.sodGO2m2d * sodResuspFactor;
    const sodKgO2h_dist    = (effectiveSOD_gm2 * zoneAreaM2 / 1000) / 24;
    const totalDemandDist  = sodKgO2h_dist + bodKgO2h;
    const oxygenSurplusDist = aotr - totalDemandDist;

    // Proportional flow in loop — ring main criterion: half-pipe velocity < 15 m/s
    const flowProportionOk = v_pipe_actual < 15;

    // Validation checks
    const checks = {
      bubbleSize:    d_b_mm_dist < 2.0,    // <2 mm fine bubble (disturbed diameter)
      bedVelocity:   disturbRatio < 1.0,   // Shields below critical → no resuspension
      coverage:      coveragePct >= 60,
      powerDensity:  powerDensWm3 >= 0.5 && powerDensWm3 <= 10,
      oxygenBalance: oxygenSurplusDist > 0, // must meet disturbed SOD
      soteReasonable: sote_pct >= 10 && sote_pct <= 80,
    };

    // Reference bubble at 20 °C for seasonal comparison
    const d_b_mm_20 = bubbleDiamMm(d.poreSizeUm, d.airflowPerDiffNm3h, activePores, 20);
    const v_rise_b_20 = riseVelocityMs(d_b_mm_20, 20);
    const d_b_surf_20 = bubbleDiamAtSurface(d_b_mm_20, effSubm, 20);

    return {
      totalDiffusers, effSubm, activePores,
      d_b_mm, d_b_surf, v_rise_b, v_rise_s, Re_b, riseTimeS,
      P_bed, P_mid, Cs_mid,
      sote_pct, totalNm3h, sotr, aotr,
      zoneAreaM2, zoneVolM3, sodKgO2h, bodKgO2h, totalDemandKgO2h, oxygenSurplus,
      coveragePerDiff, totalCoverage, coveragePct,
      ΔP_static, frictionLossKpa, blowerPressKpa, blowerPressTotal,
      id_needed, dn_sel, id_actual, v_pipe_actual,
      kw, sae, powerDensWm3,
      v_bed, flowProportionOk, checks,
      // Bed sedimentation disturbance
      tau_bed, u_star, shields, shields_cr, disturbRatio, resuspRisk, resuspColor,
      turbCorr_b, d_b_mm_dist, d_b_surf_dist,
      sodResuspFactor, effectiveSOD_gm2, sodKgO2h_dist, totalDemandDist, oxygenSurplusDist,
      // Water properties at current temperature
      rho_w, mu_w, sigma_w, nu_w,
      // Reference at 20 °C for seasonal delta comparison
      d_b_mm_20, v_rise_b_20, d_b_surf_20,
    };
  }, [d, ALL_DIFFUSER_TYPES, customDiff]);

  const fmt = (v: number, dp = 2) => isNaN(v) ? '—' : v.toFixed(dp);
  const fmtK = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : fmt(v, 0);

  const TABS = [
    { id: 'design',    label: 'Design Inputs',    icon: Settings },
    { id: 'airblower', label: 'Air & Blower',      icon: Wind },
    { id: 'physics',   label: 'Physics & Results', icon: Activity },
  ] as const;

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      <PageHeader
        icon={Wind}
        iconColor="text-cyan-400"
        title="Reservoir Diffused Aeration"
        subtitle={siteName ? `Site: ${siteName}` : 'Bottom-mounted fine-bubble aeration design'}
        badge={
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-cyan-500/10 text-cyan-400 border border-cyan-800/40">
            Submerged Diffuser
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button onClick={() => setD(DEFAULTS)}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg transition-colors flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        }
      />

      {/* System summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <KpiCard label="Diffusers"         value={String(results.totalDiffusers)} unit="total"      color="#06b6d4" />
        <KpiCard label="Bubble ∅ (bed)"    value={fmt(results.d_b_mm, 2)}        unit="mm"         color="#8b5cf6"
          warn={results.d_b_mm > 3} tip="Bubble diameter at orifice. Target <2 mm for fine bubble." />
        <KpiCard label="Bubble ∅ surface"  value={fmt(results.d_b_surf, 3)}      unit={`mm ×${fmt(results.d_b_surf/Math.max(results.d_b_mm,0.001),2)} exp.`} color="#a78bfa"
          tip={`Bubble expands from ${fmt(results.d_b_mm,3)} mm at bed to ${fmt(results.d_b_surf,3)} mm at surface (ideal gas expansion, P_bed=${fmt(results.P_bed/1000,1)} kPa).`} />
        <KpiCard label="Rise Velocity"     value={fmt(results.v_rise_b, 3)}      unit="m/s"        color="#10b981" />
        <KpiCard label="SOTR"             value={fmt(results.sotr, 2)}           unit="kg O₂/h"   color="#f59e0b" />
        <KpiCard label="AOTR"             value={fmt(results.aotr, 2)}           unit="kg O₂/h"   color="#f97316" />
        <KpiCard label="Blower Power"      value={fmt(results.kw, 1)}            unit="kW"         color="#ef4444" />
        <KpiCard label="SAE"              value={fmt(results.sae, 2)}            unit="kgO₂/kWh"  color="#22c55e" />
      </div>

      {/* Validation check strip */}
      <div className="rb-card p-4">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Design Checks</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <CheckRow label="Fine bubble disturbed (<2 mm)"   ok={results.checks.bubbleSize}    value={fmt(results.d_b_mm_dist, 3)} limit="2.0"  unit="mm" />
          <CheckRow label="Bed Shields θ/θ_cr (<1.0)"       ok={results.checks.bedVelocity}  value={fmt(results.disturbRatio, 2)} limit="1.0"   unit="×" />
          <CheckRow label="Zone coverage (≥60%)"             ok={results.checks.coverage}      value={fmt(results.coveragePct, 0)}  limit="60"   unit="%" />
          <CheckRow label="Power density (0.5–10 W/m³)"      ok={results.checks.powerDensity} value={fmt(results.powerDensWm3, 2)} limit="0.5–10" unit="W/m³" />
          <CheckRow label="O₂ balance w/ disturbance"        ok={results.checks.oxygenBalance} value={`${results.oxygenSurplusDist >= 0 ? '+' : ''}${fmt(results.oxygenSurplusDist, 2)}`} limit="0" unit="kg/h" />
          <CheckRow label="SOTE reasonable (10–80%)"         ok={results.checks.soteReasonable} value={fmt(results.sote_pct, 1)} limit="10–80" unit="%" />
        </div>
      </div>

      {/* ── Seasonal Raw Water Profile Selector ── */}
      <div className="rb-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-slate-200 flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-amber-400" />
              Seasonal Raw Water Profile
              {selectedSeason && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                  {SEASONAL_PROFILES.find(s => s.id === selectedSeason)?.icon}{' '}
                  {SEASONAL_PROFILES.find(s => s.id === selectedSeason)?.label} active
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Select the season to load UK lowland eutrophic reservoir raw water data — applies temperature, DO, α factor and oxygen demand to calculations.
              Source: EA / UKCEH / DWI / Müller et al. (2012) / Woolway et al. (2021)
            </p>
          </div>
          {selectedSeason && (
            <button onClick={() => setShowSeasonData(s => !s)}
              className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors ml-4 shrink-0">
              {showSeasonData ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showSeasonData ? 'Hide data' : 'Show water quality data'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SEASONAL_PROFILES.map(profile => (
            <SeasonCard
              key={profile.id}
              profile={profile}
              selected={selectedSeason === profile.id}
              onSelect={() => applySeason(profile)}
            />
          ))}
        </div>

        {selectedSeason && showSeasonData && (
          <SeasonalDataPanel
            profile={SEASONAL_PROFILES.find(s => s.id === selectedSeason)!}
          />
        )}

        {!selectedSeason && (
          <div className="mt-3 p-3 rounded-xl bg-slate-800/30 border border-slate-800/50 text-[11px] text-slate-600 text-center">
            Select a season above to pre-load UK reservoir water quality data into your aeration design. You can adjust any value manually after applying.
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-rb-elevated rounded-2xl border border-slate-800/50">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                active ? 'bg-rb-bg text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'
              }`}>
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── TAB 1: Design Inputs ── */}
      {activeTab === 'design' && (
        <div className="space-y-4">

          {/* Zone configuration */}
          <SectionCard title="Aeration Zone Configuration" icon={Database} iconColor="text-cyan-400">
            <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-sky-500/5 border border-sky-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-sky-200/70">
                Define the zone of the reservoir where diffusers will be installed. Aeration does not need to cover the entire water body —
                focus on the deepest areas and anoxic hotspots. Recommended power density: <strong className="text-sky-300">1–8 W/m³</strong>.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Zone Name</label>
                <input value={d.zoneName} onChange={e => upd({ zoneName: e.target.value })}
                  className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500" />
              </div>
              <NumInput label="Zone Area" unit="ha"  value={d.zoneAreaHa}   onChange={v => upd({ zoneAreaHa: v })}   step={0.1} min={0.1}
                tip="Surface area of aeration zone in hectares" />
              <NumInput label="Water Depth (average)" unit="m" value={d.waterDepthM} onChange={v => upd({ waterDepthM: v })} step={0.1} min={0.5}
                tip="Mean water depth in the aeration zone. Drives static pressure and effective submergence." />
            </div>
            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>Zone area <strong className="text-cyan-400">{fmt(results.zoneAreaM2 / 1e4, 2)} ha</strong> = {fmtK(results.zoneAreaM2)} m²</div>
              <div>Zone volume <strong className="text-sky-400">{fmt(results.zoneVolM3 / 1000, 1)} ML</strong></div>
              <div>Eff. submergence <strong className="text-amber-400">{fmt(results.effSubm, 2)} m</strong></div>
              <div>Bed pressure <strong className="text-violet-400">{fmt(results.P_bed / 1000, 1)} kPa</strong></div>
            </div>
          </SectionCard>

          {/* Diffuser specification */}
          <SectionCard title="Diffuser Specification" icon={Circle} iconColor="text-violet-400">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="sm:col-span-2">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Diffuser Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {ALL_DIFFUSER_TYPES.map(t => (
                    <button key={t.value} onClick={() => {
                      // When user picks a diffuser type, auto-update pore size and
                      // diameter to the typical values for that type so bubble size
                      // visibly responds. Custom diffuser keeps its own values.
                      const patch: Partial<AerationDesign> = { diffuserType: t.value };
                      const tDef = t as { typicalPoreUm?: number; typicalDiamMm?: number };
                      if (tDef.typicalPoreUm !== undefined) patch.poreSizeUm = tDef.typicalPoreUm;
                      if (tDef.typicalDiamMm !== undefined) patch.diffuserDiamMm = tDef.typicalDiamMm;
                      upd(patch);
                    }}
                      className={`text-left px-3 py-2 rounded-xl border text-xs transition-all ${
                        d.diffuserType === t.value
                          ? t.value === 'custom'
                            ? 'bg-emerald-500/10 border-emerald-500/60 text-emerald-300'
                            : 'bg-violet-500/10 border-violet-500/60 text-violet-300'
                          : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                      }`}>
                      <div className="font-bold">{t.label}</div>
                      <div className="text-[10px] mt-0.5 opacity-70 line-clamp-2">{t.notes}</div>
                      <div className="text-[10px] mt-1 font-mono text-emerald-400">SOTE {t.sote.toFixed(1)}%/m</div>
                    </button>
                  ))}
                </div>
                {!customDiff && (
                  <div className="mt-2 text-[10px] text-slate-500 italic">
                    Tip: design a custom diffuser on the Custom Diffuser page — it will appear here automatically.
                  </div>
                )}
                {d.diffuserType === 'custom' && customDiff && (
                  <div className="mt-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[10px] text-emerald-300">
                    <strong>Custom diffuser active:</strong> {customDiff.notes} ·{' '}
                    pore {customDiff.poreSize} µm · Ø{customDiff.diameterMm} mm · {customDiff.activePores.toLocaleString()} pores
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <NumInput label="Disc Diameter / Length" unit="mm" value={d.diffuserDiamMm}
                onChange={v => upd({ diffuserDiamMm: v })} step={10} min={100} max={1000}
                tip="Diameter of disc diffuser, or effective length of tube diffuser (mm)" />
              <NumInput label="Pore Size" unit="µm" value={d.poreSizeUm}
                onChange={v => upd({ poreSizeUm: v })} step={5} min={20} max={500}
                tip="Membrane pore size. 50–100 µm → fine bubble. 100–250 µm → medium-fine. Below 1 mm bubble needs ≤100 µm." />
              <NumInput label="Airflow per Diffuser" unit="Nm³/h" value={d.airflowPerDiffNm3h}
                onChange={v => upd({ airflowPerDiffNm3h: v })} step={0.1} min={0.1} max={20}
                tip="Air supplied to each diffuser at standard conditions. 0.5–2 Nm³/h typical for fine-bubble discs." />
              <NumInput label="Diffuser ΔP" unit="kPa" value={d.diffuserDropKpa}
                onChange={v => upd({ diffuserDropKpa: v })} step={0.1} min={0} max={20}
                tip="Pressure drop across diffuser at nominal flow. Membrane disc: 0.5–3 kPa. Ceramic: 1–5 kPa." />
              <NumInput label="Mounting Height" unit="m" value={d.mountingHeightM}
                onChange={v => upd({ mountingHeightM: v })} step={0.05} min={0.1} max={1.0}
                tip="Height of diffuser above reservoir bed. 0.3–0.5 m recommended (prevents bed scour). Default 0.4 m." />
            </div>

            <div className="mt-3 p-3 rounded-xl bg-violet-500/5 border border-violet-500/20 text-xs text-slate-400 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>Active pores <strong className="text-violet-300">{fmtK(results.activePores)}</strong></div>
              <div>Per-pore flow <strong className="text-violet-300">{fmt((d.airflowPerDiffNm3h / results.activePores) * 1e6, 2)} µL/s</strong></div>
              <div>SOTE/m <strong className="text-emerald-400">{fmt(ALL_DIFFUSER_TYPES.find(t => t.value === d.diffuserType)?.sote ?? 0, 1)}%</strong></div>
              <div>Total SOTE <strong className="text-emerald-400">{fmt(results.sote_pct, 1)}%</strong> at {fmt(results.effSubm, 1)} m</div>
            </div>
          </SectionCard>

          {/* Loop layout */}
          <SectionCard title="Loop Layout & Coverage" icon={Layers} iconColor="text-amber-400">
            <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/70">
                A <strong className="text-amber-300">ring main (loop)</strong> provides proportional airflow to each diffuser by equalising pressure
                along a closed header pipe — each diffuser sees the same differential pressure. Spacing 15–25 m in ≤6 m depth per the Barcombe case study.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <NumInput label="Diffusers per Loop" value={d.diffusersPerLoop}
                onChange={v => upd({ diffusersPerLoop: Math.max(1, Math.round(v)) })} step={1} min={1}
                tip="Number of diffusers connected to a single continuous air loop" />
              <NumInput label="Number of Loops" value={d.numberOfLoops}
                onChange={v => upd({ numberOfLoops: Math.max(1, Math.round(v)) })} step={1} min={1}
                tip="Parallel independent loops run from the blower header. Each loop is proportionally balanced." />
              <NumInput label="Diffuser Spacing" unit="m" value={d.spacingM}
                onChange={v => upd({ spacingM: v })} step={0.5} min={1}
                tip="Centre-to-centre distance between adjacent diffusers. 15–25 m for depths ≤6 m; 25–40 m for deeper." />
              <div className="flex flex-col justify-end">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Header Type</label>
                <div className="flex gap-2">
                  <button onClick={() => upd({ ringMain: true })}
                    className={`flex-1 py-2 rounded-xl border text-xs font-bold transition-all ${
                      d.ringMain ? 'bg-amber-500/10 border-amber-500/60 text-amber-300' : 'bg-rb-elevated border-slate-700/60 text-slate-400'
                    }`}>Ring Main</button>
                  <button onClick={() => upd({ ringMain: false })}
                    className={`flex-1 py-2 rounded-xl border text-xs font-bold transition-all ${
                      !d.ringMain ? 'bg-amber-500/10 border-amber-500/60 text-amber-300' : 'bg-rb-elevated border-slate-700/60 text-slate-400'
                    }`}>Radial</button>
                </div>
              </div>
            </div>
            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>Total diffusers <strong className="text-amber-400">{results.totalDiffusers}</strong></div>
              <div>Coverage/diff. <strong className="text-sky-400">{fmt(results.coveragePerDiff, 0)} m²</strong></div>
              <div>Total coverage <strong className="text-sky-400">{fmt(results.totalCoverage / 1e4, 2)} ha</strong></div>
              <div>Zone coverage <strong className={results.coveragePct >= 60 ? 'text-emerald-400' : 'text-amber-400'}>{fmt(results.coveragePct, 0)}%</strong></div>
            </div>
          </SectionCard>

          {/* Process conditions */}
          <SectionCard title="Process & Oxygen Demand" icon={FlaskConical} iconColor="text-emerald-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <NumInput label="Water Temperature" unit="°C" value={d.waterTempC}
                onChange={v => upd({ waterTempC: v })} step={0.5} min={0} max={35}
                tip="Operating temperature for DO saturation and oxygen transfer corrections" />
              <NumInput label="Current DO" unit="mg/L" value={d.currentDoMgL}
                onChange={v => upd({ currentDoMgL: v })} step={0.1} min={0} max={14}
                tip="Existing dissolved oxygen in reservoir. Lower → greater AOTR driving force." />
              <NumInput label="α (alpha)" value={d.alphaFactor}
                onChange={v => upd({ alphaFactor: v })} step={0.01} min={0.3} max={1.0}
                tip="Process-water correction for oxygen transfer vs clean water. Reservoirs: 0.6–0.85" />
              <NumInput label="β (beta)" value={d.betaFactor}
                onChange={v => upd({ betaFactor: v })} step={0.01} min={0.8} max={1.0}
                tip="Salinity/surface tension correction. Freshwater reservoirs: 0.95–1.00" />
              <NumInput label="SOD" unit="g O₂/m²/d" value={d.sodGO2m2d}
                onChange={v => upd({ sodGO2m2d: v })} step={0.1} min={0}
                tip="Sediment oxygen demand. Organic-rich sediment: 0.5–2.0 g/m²/day. Clean gravel: 0.1–0.3" />
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── TAB 2: Air & Blower ── */}
      {activeTab === 'airblower' && (
        <div className="space-y-4">

          {/* Air main pipe sizing */}
          <SectionCard title="Air Supply Main — Pipe Sizing" icon={Ruler} iconColor="text-sky-400">
            <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-sky-500/5 border border-sky-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-sky-200/70">
                Air is supplied from a shore-mounted blower through a submerged HDPE pipe header.
                For a <strong className="text-sky-300">ring main</strong>, each half carries Q/2 — select pipe diameter for target velocity.
                Recommended air velocity: <strong className="text-sky-300">8–12 m/s</strong> in main header, 4–8 m/s in laterals.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              <NumInput label="Target Pipe Velocity" unit="m/s" value={d.pipeVelocityMs}
                onChange={v => upd({ pipeVelocityMs: v })} step={0.5} min={3} max={20}
                tip="Air velocity in the main header. Higher = smaller pipe but more friction. 8–12 m/s recommended." />
              <NumInput label="Pipe Run Length" unit="m" value={d.pipeRunLengthM}
                onChange={v => upd({ pipeRunLengthM: v })} step={5} min={10}
                tip="Straight-run length from blower to furthest diffuser. Add 20–30% for fittings." />
              <NumInput label="Friction Factor (f)" value={d.pipeFrictionFactor}
                onChange={v => upd({ pipeFrictionFactor: v })} step={0.001} min={0.01} max={0.05}
                tip="Darcy-Weisbach friction factor. HDPE smooth pipe: 0.018–0.025 (use 0.022 default)." />
              <div className="flex flex-col justify-end">
                <div className="p-3 rounded-xl bg-sky-500/5 border border-sky-500/20 text-xs text-slate-400">
                  <div className="font-bold text-sky-300 text-sm mb-2">Selected Pipe</div>
                  <div>Min ID needed <strong className="text-sky-300">{fmt(results.id_needed * 1000, 1)} mm</strong></div>
                  <div>Selected <strong className="text-amber-400">DN {results.dn_sel}</strong> HDPE SDR11</div>
                  <div>Actual ID <strong className="text-amber-400">{fmt(results.id_actual * 1000, 1)} mm</strong></div>
                </div>
              </div>
            </div>

            {/* Pipe sizing results */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Total Air Flow</div>
                <div className="text-lg font-bold font-mono text-cyan-400">{fmt(results.totalNm3h, 1)}</div>
                <div className="text-[10px] text-slate-600">Nm³/h</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Actual Pipe Velocity</div>
                <div className={`text-lg font-bold font-mono ${results.v_pipe_actual < 15 ? 'text-emerald-400' : 'text-amber-400'}`}>{fmt(results.v_pipe_actual, 1)}</div>
                <div className="text-[10px] text-slate-600">m/s in pipe</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Friction Loss</div>
                <div className="text-lg font-bold font-mono text-violet-400">{fmt(results.frictionLossKpa, 2)}</div>
                <div className="text-[10px] text-slate-600">kPa</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Ring Main</div>
                <div className={`text-lg font-bold ${d.ringMain ? 'text-emerald-400' : 'text-slate-400'}`}>{d.ringMain ? 'Yes' : 'No'}</div>
                <div className="text-[10px] text-slate-600">proportional flow</div>
              </div>
            </div>

            {/* Pressure budget breakdown */}
            <div className="mt-4">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Blower Pressure Budget</div>
              <div className="space-y-1.5">
                {[
                  { label: 'Static head (water depth)', val: results.ΔP_static, color: 'bg-sky-500', pct: results.ΔP_static / results.blowerPressTotal * 100 },
                  { label: 'Pipe friction losses',       val: results.frictionLossKpa,    color: 'bg-violet-500', pct: results.frictionLossKpa / results.blowerPressTotal * 100 },
                  { label: 'Diffuser pressure drop',     val: d.diffuserDropKpa,          color: 'bg-amber-500',  pct: d.diffuserDropKpa / results.blowerPressTotal * 100 },
                  { label: `Safety margin (+${d.safetyMarginPct}%)`, val: results.blowerPressTotal - results.blowerPressKpa, color: 'bg-slate-500', pct: (results.blowerPressTotal - results.blowerPressKpa) / results.blowerPressTotal * 100 },
                ].map(row => (
                  <div key={row.label} className="flex items-center gap-3">
                    <div className="text-xs text-slate-400 w-52 shrink-0">{row.label}</div>
                    <div className="flex-1 h-4 bg-rb-elevated rounded-full overflow-hidden">
                      <div className={`h-full ${row.color} opacity-70 rounded-full`} style={{ width: `${Math.min(100, row.pct)}%` }} />
                    </div>
                    <div className="text-xs font-mono text-slate-300 w-20 text-right">{fmt(row.val, 2)} kPa</div>
                  </div>
                ))}
                <div className="flex items-center justify-end pt-1 border-t border-slate-800/50">
                  <div className="text-sm font-bold text-amber-400 font-mono">{fmt(results.blowerPressTotal, 2)} kPa total</div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Blower selection */}
          <SectionCard title="Blower Design" icon={Gauge} iconColor="text-amber-400">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {BLOWER_TYPES.map(bt => (
                <button key={bt.value} onClick={() => upd({ blowerType: bt.value, blowerEfficiency: bt.efficiency })}
                  className={`text-left px-3 py-2 rounded-xl border text-xs transition-all ${
                    d.blowerType === bt.value
                      ? 'bg-amber-500/10 border-amber-500/60 text-amber-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>
                  <div className="font-bold">{bt.label}</div>
                  <div className="text-[10px] mt-0.5 opacity-70">{bt.notes}</div>
                  <div className="text-[10px] mt-1 font-mono text-sky-400">η = {(bt.efficiency * 100).toFixed(0)}%</div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <NumInput label="Blower Efficiency (η)" value={d.blowerEfficiency}
                onChange={v => upd({ blowerEfficiency: v })} step={0.01} min={0.3} max={0.95}
                tip="Mechanical efficiency of blower. Roots: 0.60–0.70, Screw: 0.72–0.82, Centrifugal: 0.78–0.85" />
              <NumInput label="Safety Margin" unit="%" value={d.safetyMarginPct}
                onChange={v => upd({ safetyMarginPct: v })} step={1} min={0} max={30}
                tip="Additional pressure margin above calculated requirement. 10% typical." />
            </div>

            {/* Blower output card */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rb-section px-3 py-3 col-span-2 sm:col-span-1">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Discharge Pressure</div>
                <div className="text-2xl font-bold font-mono text-amber-400">{fmt(results.blowerPressTotal, 1)}</div>
                <div className="text-[10px] text-slate-600">kPa = {fmt(results.blowerPressTotal / 100, 3)} bar(g)</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Shaft Power</div>
                <div className="text-2xl font-bold font-mono text-red-400">{fmt(results.kw, 1)}</div>
                <div className="text-[10px] text-slate-600">kW</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Annual Energy</div>
                <div className="text-2xl font-bold font-mono text-orange-400">{fmtK(results.kw * 8760)}</div>
                <div className="text-[10px] text-slate-600">kWh/yr</div>
              </div>
              <div className="rb-section px-3 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider">Power Density</div>
                <div className={`text-2xl font-bold font-mono ${results.checks.powerDensity ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {fmt(results.powerDensWm3, 2)}
                </div>
                <div className="text-[10px] text-slate-600">W/m³ zone (1–8 W/m³ ok)</div>
              </div>
            </div>

            <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-slate-400">
              <strong className="text-amber-300">Recommended blower specification:</strong>
              {' '}{BLOWER_TYPES.find(b => b.value === d.blowerType)?.label}
              {' '}— flow <strong className="text-slate-200">{fmt(results.totalNm3h, 1)} Nm³/h</strong>
              {' '}at discharge pressure <strong className="text-slate-200">{fmt(results.blowerPressTotal, 1)} kPa</strong>
              {' '}({fmt(results.blowerPressTotal / 100, 3)} bar) gauge.
              {' '}Motor: <strong className="text-slate-200">{fmt(results.kw * 1.25, 1)} kW</strong> (shaft power × 1.25 motor oversizing factor).
              {' '}Shore-mounted in weatherproof GRP enclosure with inlet silencer.
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── TAB 3: Physics & Results ── */}
      {activeTab === 'physics' && (
        <div className="space-y-4">

          {/* Temperature-dependent water properties card */}
          <SectionCard title={`Water Properties at ${d.waterTempC} °C`} icon={Thermometer} iconColor="text-sky-400">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { label: 'Density ρ_w', val: results.rho_w, ref: RHO_W_20, unit: 'kg/m³', dp: 2,
                  effect: 'Higher density at low T → slightly higher hydrostatic pressure. Small effect on bubble diameter (ρ in Fritz denominator).' },
                { label: 'Viscosity μ_w', val: results.mu_w * 1000, ref: MU_W_20 * 1000, unit: '×10⁻³ Pa·s', dp: 3,
                  effect: 'Dominant seasonal effect on rise velocity. Cold water 4°C is 56% more viscous → bubble rises ~73% as fast. v ∝ ν^−0.7.' },
                { label: 'Surface tension σ', val: results.sigma_w * 1000, ref: waterSurfaceTension(20) * 1000, unit: '×10⁻³ N/m', dp: 2,
                  effect: 'Drives bubble formation size in Fritz correlation: d_b ∝ σ^(1/3). Cold water (higher σ) → slightly larger bubbles at orifice.' },
                { label: 'Kin. viscosity ν', val: results.nu_w * 1e6, ref: NU_W_20 * 1e6, unit: '×10⁻⁶ m²/s', dp: 3,
                  effect: 'Used directly in rise velocity temperature correction: v(T)/v(20°C) = (ν_20/ν_T)^0.7.' },
              ].map(p => {
                const delta = ((p.val - p.ref) / p.ref) * 100;
                const isHigher = p.val > p.ref;
                return (
                  <div key={p.label} className="rb-section px-3 py-3" title={p.effect}>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">{p.label}</div>
                    <div className="text-xl font-bold font-mono text-sky-300">{p.val.toFixed(p.dp)}</div>
                    <div className="text-[10px] text-slate-600">{p.unit}</div>
                    <div className={`mt-1 text-[10px] font-mono font-bold ${isHigher ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {isHigher ? '+' : ''}{delta.toFixed(1)}% vs 20 °C
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Seasonal bubble comparison */}
            <div className="p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">
                Seasonal Effect on Bubble Size & Velocity vs Standard (20 °C)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Bubble at bed */}
                <div className="space-y-1">
                  <div className="text-[10px] text-slate-500 mb-1">Bubble diameter at bed</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-rb-bg rounded-full overflow-hidden relative">
                      <div className="h-full bg-violet-500/30 rounded-full" style={{ width: `${Math.min(100, (results.d_b_mm_20 / 3) * 100)}%` }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-400">
                        20°C: {results.d_b_mm_20.toFixed(3)} mm
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 rounded-full overflow-hidden relative"
                      style={{ background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'22' : '#334155' }}>
                      <div className="h-full rounded-full" style={{
                        width: `${Math.min(100, (results.d_b_mm / 3) * 100)}%`,
                        background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'88' : '#64748b'
                      }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-300">
                        {d.waterTempC}°C: {results.d_b_mm.toFixed(3)} mm
                        <span className={`ml-1 text-[9px] ${results.d_b_mm > results.d_b_mm_20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          ({results.d_b_mm > results.d_b_mm_20 ? '+' : ''}{((results.d_b_mm - results.d_b_mm_20)/results.d_b_mm_20*100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bubble at surface */}
                <div className="space-y-1">
                  <div className="text-[10px] text-slate-500 mb-1">Bubble diameter at surface</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-rb-bg rounded-full overflow-hidden relative">
                      <div className="h-full bg-violet-400/30 rounded-full" style={{ width: `${Math.min(100, (results.d_b_surf_20 / 3) * 100)}%` }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-400">
                        20°C: {results.d_b_surf_20.toFixed(3)} mm
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 rounded-full overflow-hidden relative"
                      style={{ background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'22' : '#334155' }}>
                      <div className="h-full rounded-full" style={{
                        width: `${Math.min(100, (results.d_b_surf / 3) * 100)}%`,
                        background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'88' : '#64748b'
                      }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-300">
                        {d.waterTempC}°C: {results.d_b_surf.toFixed(3)} mm
                        <span className={`ml-1 text-[9px] ${results.d_b_surf > results.d_b_surf_20 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          ({results.d_b_surf > results.d_b_surf_20 ? '+' : ''}{((results.d_b_surf - results.d_b_surf_20)/results.d_b_surf_20*100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Rise velocity */}
                <div className="space-y-1">
                  <div className="text-[10px] text-slate-500 mb-1">Rise velocity (at bed)</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-rb-bg rounded-full overflow-hidden relative">
                      <div className="h-full bg-emerald-500/30 rounded-full" style={{ width: `${Math.min(100, results.v_rise_b_20 / 0.4 * 100)}%` }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-400">
                        20°C: {results.v_rise_b_20.toFixed(3)} m/s
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 rounded-full overflow-hidden relative"
                      style={{ background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'22' : '#334155' }}>
                      <div className="h-full rounded-full" style={{
                        width: `${Math.min(100, results.v_rise_b / 0.4 * 100)}%`,
                        background: selectedSeason ? SEASONAL_PROFILES.find(s=>s.id===selectedSeason)?.accentHex+'88' : '#64748b'
                      }} />
                      <div className="absolute inset-0 flex items-center px-2 text-[10px] font-mono text-slate-300">
                        {d.waterTempC}°C: {results.v_rise_b.toFixed(3)} m/s
                        <span className={`ml-1 text-[9px] ${results.v_rise_b > results.v_rise_b_20 ? 'text-emerald-400' : 'text-amber-400'}`}>
                          ({results.v_rise_b > results.v_rise_b_20 ? '+' : ''}{((results.v_rise_b - results.v_rise_b_20)/results.v_rise_b_20*100).toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-slate-600">
                Cold winter water is more viscous — bubbles rise slower (longer contact time → better O₂ transfer).
                Higher surface tension in winter gives marginally larger bubbles at the orifice.
                Warmer summer water has lower viscosity → faster rise → less O₂ transfer per bubble — but the near-anoxic hypolimnion driving force compensates.
              </div>
            </div>
          </SectionCard>

          {/* Bubble physics */}
          <SectionCard title="Bubble Physics at Diffuser Depth" icon={Activity} iconColor="text-violet-400">
            <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-violet-500/5 border border-violet-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-violet-200/70">
                Bubble diameter is calculated from the Fritz (1935) orifice correlation (static) with a dynamic correction for per-pore airflow.
                Rise velocity uses an empirical fit for <em>contaminated</em> water (Clift, Grace &amp; Weber 1978) — reservoirs have surface-active compounds
                that suppress internal circulation, causing bubbles to behave more like rigid spheres.
                Pressure expansion from bed to surface is from ideal gas law.
              </p>
            </div>

            {/* Main bubble metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Bubble ∅ at Orifice</div>
                <div className={`text-2xl font-bold font-mono ${results.d_b_mm < 2 ? 'text-violet-400' : 'text-amber-400'}`}>{fmt(results.d_b_mm, 3)}</div>
                <div className="text-[10px] text-slate-500">mm at {fmt(results.effSubm, 1)} m depth</div>
                <div className={`mt-1 text-[10px] font-bold ${results.d_b_mm < 1 ? 'text-emerald-400' : results.d_b_mm < 2 ? 'text-sky-400' : 'text-amber-400'}`}>
                  {results.d_b_mm < 1 ? '✓ Ultra-fine <1 mm' : results.d_b_mm < 2 ? '✓ Fine bubble' : results.d_b_mm < 5 ? '⚠ Medium bubble' : '✗ Coarse bubble'}
                </div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Bubble ∅ at Surface</div>
                <div className="text-2xl font-bold font-mono text-violet-300">{fmt(results.d_b_surf, 3)}</div>
                <div className="text-[10px] text-slate-500">mm — expanded by pressure</div>
                <div className="mt-1 text-[10px] text-slate-600">
                  ×{fmt(results.d_b_surf / results.d_b_mm, 2)} expansion ratio
                </div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Rise Velocity (bed)</div>
                <div className={`text-2xl font-bold font-mono ${results.v_rise_b < 0.35 ? 'text-emerald-400' : 'text-amber-400'}`}>{fmt(results.v_rise_b, 3)}</div>
                <div className="text-[10px] text-slate-500">m/s at diffuser depth</div>
                <div className="mt-1 text-[10px] text-slate-600">Target: &lt;0.35 m/s</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Rise Velocity (surface)</div>
                <div className="text-2xl font-bold font-mono text-emerald-300">{fmt(results.v_rise_s, 3)}</div>
                <div className="text-[10px] text-slate-500">m/s at water surface</div>
                <div className="mt-1 text-[10px] text-slate-600">Larger bubble → faster</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Rise Time</div>
                <div className="text-2xl font-bold font-mono text-sky-400">{fmt(results.riseTimeS, 1)}</div>
                <div className="text-[10px] text-slate-500">seconds bottom→surface</div>
                <div className="mt-1 text-[10px] text-slate-600">More time = more O₂ transfer</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Reynolds Number</div>
                <div className="text-2xl font-bold font-mono text-slate-300">{fmt(results.Re_b, 0)}</div>
                <div className="text-[10px] text-slate-500">at orifice diameter</div>
                <div className="mt-1 text-[10px] text-slate-600">{results.Re_b < 1 ? 'Stokes' : results.Re_b < 1000 ? 'Intermediate' : 'Turbulent'}</div>
              </div>
              <div className={`rb-section px-4 py-3 ${results.v_bed > 0.025 ? 'border-amber-500/40' : ''}`}>
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Induced Bed Velocity</div>
                <div className={`text-2xl font-bold font-mono ${results.v_bed < 0.015 ? 'text-emerald-400' : results.v_bed < 0.025 ? 'text-sky-400' : 'text-amber-400'}`}>
                  {fmt(results.v_bed * 1000, 1)}
                </div>
                <div className="text-[10px] text-slate-500">mm/s horizontal at bed</div>
                <div className={`mt-1 text-[10px] font-bold ${results.v_bed < 0.015 ? 'text-emerald-400' : results.v_bed < 0.025 ? 'text-sky-400' : 'text-amber-400'}`}>
                  {results.v_bed < 0.015 ? '✓ Safe' : results.v_bed < 0.025 ? '~ Monitor' : '✗ Resuspension risk'}
                </div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Bed Pressure</div>
                <div className="text-2xl font-bold font-mono text-slate-400">{fmt(results.P_bed / 1000, 1)}</div>
                <div className="text-[10px] text-slate-500">kPa absolute</div>
                <div className="mt-1 text-[10px] text-slate-600">= {fmt(results.P_bed / 1e5, 3)} bar abs</div>
              </div>
            </div>

            {/* Bubble trajectory visualisation — depth profile */}
            <div className="p-4 rounded-xl bg-rb-elevated border border-slate-800/50">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Bubble Profile — Bed to Surface</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-slate-400 min-w-[500px]">
                  <thead>
                    <tr className="text-[10px] text-slate-600 uppercase border-b border-slate-800/50">
                      <th className="text-left py-1">Depth (m)</th>
                      <th>Pressure (kPa)</th>
                      <th>Bubble ∅ (mm)</th>
                      <th>Rise v (m/s)</th>
                      <th>DO sat (mg/L)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[results.effSubm, results.effSubm * 0.75, results.effSubm * 0.5, results.effSubm * 0.25, 0.1].map((depth, i) => {
                      const P = P_ATM + results.rho_w * G * depth;
                      const d_this = results.d_b_mm * Math.pow(results.P_bed / P, 1 / 3);
                      const v_this = riseVelocityMs(d_this, d.waterTempC);
                      const cs = doSat(d.waterTempC) * (P / P_ATM);
                      return (
                        <tr key={i} className={`border-b border-slate-800/30 ${i === 0 ? 'bg-violet-500/5' : i === 4 ? 'bg-emerald-500/5' : ''}`}>
                          <td className="py-1.5 font-mono">{depth.toFixed(2)}</td>
                          <td className="text-center font-mono">{(P / 1000).toFixed(1)}</td>
                          <td className="text-center font-mono font-bold text-violet-300">{d_this.toFixed(3)}</td>
                          <td className="text-center font-mono text-emerald-300">{v_this.toFixed(3)}</td>
                          <td className="text-center font-mono text-sky-300">{cs.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[9px] text-slate-600 mt-2">Depth 0 = diffuser mounting height above bed. DO saturation increases at depth due to hydrostatic pressure (promotes absorption).</p>
            </div>
          </SectionCard>

          {/* Oxygen transfer */}
          <SectionCard title="Oxygen Transfer & Mass Balance" icon={BarChart3} iconColor="text-emerald-400">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">SOTE/m</div>
                <div className="text-2xl font-bold font-mono text-emerald-400">
                  {fmt(ALL_DIFFUSER_TYPES.find(t => t.value === d.diffuserType)?.sote ?? 0, 1)}
                </div>
                <div className="text-[10px] text-slate-500">%/m submergence</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Total SOTE</div>
                <div className="text-2xl font-bold font-mono text-emerald-300">{fmt(results.sote_pct, 1)}</div>
                <div className="text-[10px] text-slate-500">% clean water, 20°C</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">SOTR</div>
                <div className="text-2xl font-bold font-mono text-amber-400">{fmt(results.sotr, 3)}</div>
                <div className="text-[10px] text-slate-500">kg O₂/h (standard)</div>
              </div>
              <div className="rb-section px-4 py-3">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">AOTR</div>
                <div className="text-2xl font-bold font-mono text-orange-400">{fmt(results.aotr, 3)}</div>
                <div className="text-[10px] text-slate-500">kg O₂/h (field)</div>
              </div>
            </div>

            {/* Correction factors display */}
            <div className="p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-xs text-slate-400 mb-4">
              <div className="font-bold text-slate-300 mb-2">AOTR = SOTR × α × β × θ^(T−20) × (C*_T − C_L) / C*_20</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>α = <strong className="text-sky-300">{d.alphaFactor}</strong></div>
                <div>β = <strong className="text-sky-300">{d.betaFactor}</strong></div>
                <div>θ^(T−20) = <strong className="text-sky-300">{fmt(Math.pow(1.024, d.waterTempC - 20), 3)}</strong></div>
                <div>(C*_T − C_L)/C*_20 = <strong className="text-sky-300">{fmt(Math.max(0, doSat(d.waterTempC) - d.currentDoMgL) / DO_SAT_20, 3)}</strong></div>
                <div>C*_20 = <strong className="text-amber-300">{DO_SAT_20} mg/L</strong></div>
                <div>C*_T ({d.waterTempC}°C) = <strong className="text-amber-300">{fmt(doSat(d.waterTempC), 2)} mg/L</strong></div>
                <div>C_L = <strong className="text-amber-300">{d.currentDoMgL} mg/L</strong></div>
                <div>C*_mid = <strong className="text-amber-300">{fmt(results.Cs_mid, 2)} mg/L</strong> (at depth)</div>
              </div>
            </div>

            {/* Oxygen demand breakdown */}
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Oxygen Demand vs Supply</div>
            <div className="space-y-2">
              {[
                { label: 'Sediment O₂ Demand (SOD)', val: results.sodKgO2h, color: 'bg-red-500' },
                { label: 'Water Column BOD Demand',   val: results.bodKgO2h, color: 'bg-orange-500' },
                { label: 'Total Demand',               val: results.totalDemandKgO2h, color: 'bg-amber-500', bold: true },
                { label: 'AOTR (Supply)',               val: results.aotr,   color: 'bg-emerald-500', bold: true },
              ].map(row => (
                <div key={row.label} className="flex items-center gap-3">
                  <div className={`text-xs ${row.bold ? 'font-bold text-slate-200' : 'text-slate-400'} w-52 shrink-0`}>{row.label}</div>
                  <div className="flex-1 h-3.5 bg-rb-elevated rounded-full overflow-hidden">
                    <div className={`h-full ${row.color} opacity-70 rounded-full`}
                      style={{ width: `${Math.min(100, (row.val / Math.max(results.aotr, results.totalDemandKgO2h, 0.001)) * 100)}%` }} />
                  </div>
                  <div className={`text-xs font-mono w-24 text-right ${row.bold ? 'font-bold text-slate-200' : 'text-slate-400'}`}>{fmt(row.val, 3)} kg/h</div>
                </div>
              ))}
            </div>
            <div className={`mt-3 p-3 rounded-xl border text-xs ${results.oxygenSurplusDist > 0
              ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300'
              : 'bg-red-500/8 border-red-500/30 text-red-300'}`}>
              {results.oxygenSurplusDist > 0
                ? `✓ Surplus (with disturbance): AOTR exceeds effective demand by ${fmt(results.oxygenSurplusDist, 3)} kg O₂/h`
                : `✗ Deficit (with disturbance): AOTR is ${fmt(Math.abs(results.oxygenSurplusDist), 3)} kg O₂/h below effective demand. Add diffusers, increase airflow, or reduce SOD via sediment management.`}
            </div>
          </SectionCard>

          {/* Bed sedimentation disturbance */}
          <SectionCard title="Bed Sedimentation Disturbance" icon={Gauge} iconColor="text-orange-400">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Sediment D₅₀ <span className="font-normal text-slate-600 ml-1">(µm)</span>
                </label>
                <p className="text-[9px] text-slate-600 mb-1">Fine silt ≈ 20–63 µm; sand 63–2000 µm. Coarser = harder to disturb.</p>
                <input type="number" value={d.sedimentD50Um} step={10} min={1} max={2000}
                  onChange={e => upd({ sedimentD50Um: Number(e.target.value) })}
                  className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Particle Density <span className="font-normal text-slate-600 ml-1">(kg/m³)</span>
                </label>
                <p className="text-[9px] text-slate-600 mb-1">Silica/clay ≈ 2650; organic floc ≈ 1200–1800 kg/m³.</p>
                <input type="number" value={d.sedimentRho} step={50} min={1200} max={3000}
                  onChange={e => upd({ sedimentRho: Number(e.target.value) })}
                  className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500" />
              </div>
              <div className="rb-section px-3 py-2">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Induced v_bed</div>
                <div className="text-2xl font-bold font-mono text-amber-400">{fmt(results.v_bed * 1000, 2)}</div>
                <div className="text-[10px] text-slate-500">mm/s horizontal</div>
              </div>
              <div className="rb-section px-3 py-2">
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Shear velocity u*</div>
                <div className="text-2xl font-bold font-mono text-orange-400">{fmt(results.u_star * 1000, 3)}</div>
                <div className="text-[10px] text-slate-500">mm/s</div>
              </div>
            </div>

            {/* Shields number */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {[
                { label: 'Bed shear stress τ',  val: fmt(results.tau_bed * 1000, 3), unit: 'mPa',  col: '#f97316' },
                { label: 'Shields θ (calc)',     val: fmt(results.shields, 5),         unit: '—',    col: results.shields > results.shields_cr ? '#ef4444' : '#10b981' },
                { label: 'Critical θ_cr',        val: fmt(results.shields_cr, 3),      unit: 'Shields 1936', col: '#94a3b8' },
                { label: 'θ / θ_cr ratio',       val: fmt(results.disturbRatio, 2),   unit: '×',    col: results.disturbRatio > 1 ? '#f59e0b' : '#10b981' },
              ].map(k => (
                <div key={k.label} className="rb-section px-3 py-2">
                  <div className="text-[9px] text-slate-500 uppercase tracking-wider">{k.label}</div>
                  <div className="text-lg font-bold font-mono" style={{ color: k.col }}>{k.val}</div>
                  <div className="text-[10px] text-slate-600">{k.unit}</div>
                </div>
              ))}
            </div>

            {/* Risk banner */}
            <div className={`p-3 rounded-xl border mb-3 flex items-center justify-between ${
              results.disturbRatio > 1.5 ? 'bg-red-500/8 border-red-500/30' :
              results.disturbRatio > 1.0 ? 'bg-amber-500/8 border-amber-500/30' :
              results.disturbRatio > 0.5 ? 'bg-yellow-500/8 border-yellow-500/30' :
              'bg-emerald-500/5 border-emerald-500/20'
            }`}>
              <div className="text-xs text-slate-400">
                Resuspension risk: <span className="font-bold" style={{ color: results.resuspColor }}>{results.resuspRisk}</span>
                <span className="text-slate-600 ml-2">— θ/θ_cr = {fmt(results.disturbRatio, 2)}</span>
              </div>
              <div className="flex gap-1.5 text-[9px]">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">Neg &lt;0.5</span>
                <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">Low 0.5–1</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">Mod 1–1.5</span>
                <span className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">High &gt;1.5</span>
              </div>
            </div>

            {/* Effects on bubble size and SOD */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/20 space-y-1.5">
                <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-2">Bubble Size Effect</div>
                <div className="flex justify-between"><span className="text-slate-500">Clean ∅ at orifice</span><span className="font-mono text-violet-300">{fmt(results.d_b_mm, 3)} mm</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Turbulence correction</span><span className="font-mono text-amber-300">+{fmt(results.turbCorr_b * 100, 1)}%</span></div>
                <div className="flex justify-between font-bold border-t border-slate-700/50 pt-1">
                  <span className="text-slate-300">Disturbed ∅ bed</span><span className="font-mono text-orange-300">{fmt(results.d_b_mm_dist, 3)} mm</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Disturbed ∅ surface</span><span className="font-mono text-orange-200">{fmt(results.d_b_surf_dist, 3)} mm</span></div>
              </div>
              <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20 space-y-1.5">
                <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-2">SOD Enhancement</div>
                <div className="flex justify-between"><span className="text-slate-500">Nominal SOD</span><span className="font-mono text-slate-300">{fmt(d.sodGO2m2d, 2)} g/m²/d</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Resuspension ×</span><span className="font-mono text-amber-300">{fmt(results.sodResuspFactor, 2)}×</span></div>
                <div className="flex justify-between font-bold border-t border-slate-700/50 pt-1">
                  <span className="text-slate-300">Effective SOD</span><span className="font-mono text-red-300">{fmt(results.effectiveSOD_gm2, 2)} g/m²/d</span>
                </div>
                <div className="flex justify-between"><span className="text-slate-500">Effective demand</span><span className="font-mono text-red-200">{fmt(results.totalDemandDist, 3)} kgO₂/h</span></div>
              </div>
            </div>
          </SectionCard>

          {/* Pipe sizing results */}
          <SectionCard title="Air Supply — Full Calculation Summary" icon={Ruler} iconColor="text-sky-400">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-400">
              <div className="space-y-2">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Air Flow</div>
                <div className="flex justify-between"><span>Per diffuser</span><strong className="text-slate-200 font-mono">{fmt(d.airflowPerDiffNm3h, 2)} Nm³/h</strong></div>
                <div className="flex justify-between"><span>Total diffusers</span><strong className="text-slate-200 font-mono">{results.totalDiffusers}</strong></div>
                <div className="flex justify-between"><span>Total airflow</span><strong className="text-slate-200 font-mono">{fmt(results.totalNm3h, 1)} Nm³/h</strong></div>
                <div className="flex justify-between"><span>At operating pressure</span><strong className="text-slate-200 font-mono">{fmt((results.totalNm3h / 3600) * (P_ATM / results.P_bed) * 1000, 2)} L/s</strong></div>
              </div>
              <div className="space-y-2">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pipe Selection</div>
                <div className="flex justify-between"><span>Required ID (target v)</span><strong className="text-slate-200 font-mono">{fmt(results.id_needed * 1000, 1)} mm</strong></div>
                <div className="flex justify-between"><span>Selected DN</span><strong className="text-amber-400 font-mono">DN {results.dn_sel} HDPE SDR11</strong></div>
                <div className="flex justify-between"><span>Actual ID</span><strong className="text-slate-200 font-mono">{fmt(results.id_actual * 1000, 1)} mm</strong></div>
                <div className="flex justify-between"><span>Actual pipe velocity</span><strong className={`font-mono ${results.v_pipe_actual < 15 ? 'text-emerald-400' : 'text-amber-400'}`}>{fmt(results.v_pipe_actual, 1)} m/s</strong></div>
                <div className="flex justify-between"><span>Friction loss (run)</span><strong className="text-slate-200 font-mono">{fmt(results.frictionLossKpa, 2)} kPa</strong></div>
              </div>
              <div className="space-y-2">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Pressure Budget</div>
                <div className="flex justify-between"><span>Static head</span><strong className="text-slate-200 font-mono">{fmt(results.ΔP_static, 2)} kPa</strong></div>
                <div className="flex justify-between"><span>Pipe friction</span><strong className="text-slate-200 font-mono">{fmt(results.frictionLossKpa, 2)} kPa</strong></div>
                <div className="flex justify-between"><span>Diffuser ΔP</span><strong className="text-slate-200 font-mono">{fmt(d.diffuserDropKpa, 2)} kPa</strong></div>
                <div className="flex justify-between font-bold text-slate-200"><span>Total (with {d.safetyMarginPct}% margin)</span><strong className="text-amber-400 font-mono">{fmt(results.blowerPressTotal, 2)} kPa</strong></div>
              </div>
              <div className="space-y-2">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Blower Performance</div>
                <div className="flex justify-between"><span>Type</span><strong className="text-slate-200">{BLOWER_TYPES.find(b => b.value === d.blowerType)?.label}</strong></div>
                <div className="flex justify-between"><span>Efficiency (η)</span><strong className="text-slate-200 font-mono">{(d.blowerEfficiency * 100).toFixed(0)}%</strong></div>
                <div className="flex justify-between"><span>Shaft power</span><strong className="text-red-400 font-mono">{fmt(results.kw, 2)} kW</strong></div>
                <div className="flex justify-between"><span>SAE</span><strong className="text-emerald-400 font-mono">{fmt(results.sae, 2)} kg O₂/kWh</strong></div>
                <div className="flex justify-between"><span>Power density</span><strong className={`font-mono ${results.checks.powerDensity ? 'text-emerald-400' : 'text-amber-400'}`}>{fmt(results.powerDensWm3, 2)} W/m³</strong></div>
              </div>
            </div>
          </SectionCard>

          {/* Science reference notes */}
          <div className="rb-card p-5">
            <button className="w-full flex items-center justify-between"
              onClick={() => setExpandedInfo(expandedInfo === 'science' ? null : 'science')}>
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-bold text-slate-400">Design References & Physics Notes</span>
              </div>
              {expandedInfo === 'science' ? <ChevronUp className="w-4 h-4 text-slate-600" /> : <ChevronDown className="w-4 h-4 text-slate-600" />}
            </button>
            {expandedInfo === 'science' && (
              <div className="mt-4 space-y-3 text-xs text-slate-500">
                <div><strong className="text-slate-300">Bubble formation (Fritz 1935):</strong> d_b = [6σd_pore / (ρ_w·g)]^(1/3). Dynamic correction applied for per-pore flow rate: multiplication factor ≈ 1 + 0.9·(Q_pore in µL/s)^0.25. Higher airflow per pore increases bubble size.</div>
                <div><strong className="text-slate-300">Bubble rise velocity:</strong> Empirical fit for contaminated water (surface-active substances suppress internal bubble circulation, making behaviour closer to rigid spheres). v ≈ 0.14 + 0.065·d_b (m/s, d_b in mm) for 0.5–3 mm. Fine bubbles (1–3 mm): 0.20–0.35 m/s per Barcombe case data.</div>
                <div><strong className="text-slate-300">Pressure expansion:</strong> d_surface = d_bed · (P_bed/P_atm)^(1/3). Ideal gas law — bubble grows as it rises through decreasing hydrostatic pressure.</div>
                <div><strong className="text-slate-300">SOTE:</strong> Standard Oxygen Transfer Efficiency = SOTE/m × effective submergence. Fine-bubble EPDM membrane disc: 6–7%/m (ASCE 2-06 clean water test). Field corrections via AOTR = SOTR × α × β × θ^(T-20) × (C*_T − C_L)/C*_20.</div>
                <div><strong className="text-slate-300">Induced bed velocity:</strong> Simplified Seol et al. (2007) integral plume model. Buoyancy flux B = g·Q_act (m⁴/s³). Plume velocity U_p = 1.46·(B·H)^(1/3). Horizontal bed component ≈ 0.15·U_p/√H. Critical shear for fine reservoir silt: ~0.015–0.025 m/s.</div>
                <div><strong className="text-slate-300">Pipe sizing:</strong> Darcy-Weisbach friction loss. Air density at operating conditions: ρ_air_op = ρ_STP × P_mid/P_atm × T_STP/T_op. Ring main: each half carries Q/2. Standard HDPE SDR11 DN series; ID ≈ OD × 0.833.</div>
                <div><strong className="text-slate-300">Blower power:</strong> Adiabatic compression: W = [γ/(γ−1)] × P_in × Q_std × [(P_out/P_in)^((γ−1)/γ) − 1] / η. More accurate than W = Q·ΔP for rotary positive-displacement blowers at low pressure ratios.</div>
                <div><strong className="text-slate-300">Diffuser mounting:</strong> 0.3–0.5 m above bed (default 0.4 m) prevents direct bubble impingement on sediment and allows maintenance access without full drawdown. Source: Barcombe Reservoir design guidance, aeration.docx.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
